import { useCallback, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resolveStudioAgentAlias } from "@/components/studio/studioThreadUtils";
import { toast } from "@/providers/NotificationProvider";
import { agentSpecsAPI } from "@/services/api/agentSpecs";
import { useAgentSpecStudioStore } from "@/store/agentSpecStudio";
import { useContextStore } from "@/store/context";
import { usePermissionStore } from "@/store/permissions";

export type StudioFromGoalDraft = {
  title: string;
  description?: string;
};

export function studioThreadsQueryKey(
  organizationId: string | null | undefined,
  agentId: string | null | undefined,
) {
  return ["agent-spec-studio-threads", organizationId, agentId] as const;
}

export function useAgentSpecStudioThreads(options?: {
  /** When true, keep URL `threadId` and store selection in sync. */
  syncSelection?: boolean;
}) {
  const syncSelection = options?.syncSelection ?? false;
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const organizationId = useContextStore((state) => state.org);
  const agentId = useContextStore((state) => state.deploymentId);
  const environmentName = useContextStore((state) => state.env);
  const canWriteRecipes = usePermissionStore((state) =>
    state.hasCapability("write:recipes"),
  );
  const selectedAgent = useContextStore((state) =>
    state.deployments.find((agent) => agent.id === state.deploymentId),
  );
  const selectedThreadId = useAgentSpecStudioStore(
    (state) => state.selectedThreadId,
  );
  const setSelectedThreadId = useAgentSpecStudioStore(
    (state) => state.setSelectedThreadId,
  );
  const setStudioError = useAgentSpecStudioStore((state) => state.setError);
  const requestedThreadId = searchParams.get("threadId");
  const fallbackAlias = resolveStudioAgentAlias(
    selectedAgent?.name,
    agentId ?? "agent",
  );
  const threadsQueryKey = studioThreadsQueryKey(organizationId, agentId);

  const pullQuery = useQuery({
    queryKey: [
      "agent-spec-pull",
      organizationId,
      agentId,
      environmentName,
    ],
    queryFn: () =>
      agentSpecsAPI.pull(organizationId!, agentId!, {
        environmentName: environmentName ?? undefined,
      }),
    enabled: Boolean(canWriteRecipes && organizationId && agentId),
    retry: false,
    staleTime: 60_000,
  });

  const threadsQuery = useQuery({
    queryKey: threadsQueryKey,
    queryFn: () => agentSpecsAPI.listStudioThreads(organizationId!, agentId!),
    enabled: Boolean(canWriteRecipes && organizationId && agentId),
    retry: false,
  });

  const agentAlias = pullQuery.data?.agent_alias ?? fallbackAlias;
  const threads = useMemo(
    () => threadsQuery.data?.threads ?? [],
    [threadsQuery.data],
  );

  const applyThreadSelection = useCallback(
    (
      threadId: string,
      revisionId?: string | null,
      options?: { navigateToChat?: boolean },
    ) => {
      setSelectedThreadId(threadId);
      setStudioError(null);
      const next = new URLSearchParams(searchParams);
      next.set("threadId", threadId);
      if (revisionId) next.set("revisionId", revisionId);
      else next.delete("revisionId");
      if (options?.navigateToChat) {
        navigate(`/studio/chat?${next.toString()}`);
        return;
      }
      setSearchParams(next, { replace: true });
    },
    [
      navigate,
      searchParams,
      setSearchParams,
      setSelectedThreadId,
      setStudioError,
    ],
  );

  useEffect(() => {
    if (!syncSelection) return;
    if (threads.length === 0) return;
    const requested = threads.find((thread) => thread.id === requestedThreadId);
    const current = threads.find((thread) => thread.id === selectedThreadId);
    // Prefer in-memory selection (e.g. just-created thread) over a stale URL
    // threadId so create/select cannot be overwritten by an in-flight refetch.
    const next = current ?? requested ?? threads[0];
    if (next.id === selectedThreadId && requestedThreadId === next.id) return;
    if (next.id === selectedThreadId && !requestedThreadId) {
      applyThreadSelection(next.id, next.active_revision_id);
      return;
    }
    if (next.id === selectedThreadId) return;
    applyThreadSelection(next.id, next.active_revision_id);
  }, [
    applyThreadSelection,
    requestedThreadId,
    selectedThreadId,
    syncSelection,
    threads,
  ]);

  const createThreadMutation = useMutation({
    mutationFn: () =>
      agentSpecsAPI.createStudioThread(organizationId!, agentId!, {
        agent_alias: agentAlias,
        environment_name: environmentName ?? undefined,
      }),
    onSuccess: async (created) => {
      setSelectedThreadId(created.thread.id);
      setStudioError(null);
      queryClient.setQueryData(
        [
          "agent-spec-studio-thread",
          organizationId,
          agentId,
          created.thread.id,
        ],
        created,
      );
      await queryClient.invalidateQueries({ queryKey: threadsQueryKey });
      applyThreadSelection(
        created.thread.id,
        created.revision?.id ?? created.thread.active_revision_id,
        { navigateToChat: true },
      );
      toast.success(t("agentSpec.studioCreateThreadSuccess"));
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : String(error);
      setStudioError(message);
      toast.error(message || t("agentSpec.studioError"));
    },
  });

  const createFromGoalMutation = useMutation({
    mutationFn: (goal: StudioFromGoalDraft) =>
      agentSpecsAPI.createStudioJobFromGoal(organizationId!, agentId!, {
        agent_alias: agentAlias,
        environment_name: environmentName ?? undefined,
        goal: {
          title: goal.title,
          description: goal.description,
          source: "api",
        },
      }),
    onSuccess: async (created) => {
      setSelectedThreadId(created.thread.id);
      setStudioError(null);
      await queryClient.invalidateQueries({ queryKey: threadsQueryKey });
      await queryClient.invalidateQueries({
        queryKey: [
          "agent-spec-studio-thread",
          organizationId,
          agentId,
          created.thread.id,
        ],
      });
      applyThreadSelection(
        created.thread.id,
        created.thread.active_revision_id,
        { navigateToChat: true },
      );
      toast.success(t("agentSpec.studioFromGoalSuccess"));
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : String(error);
      setStudioError(message);
      toast.error(message || t("agentSpec.studioError"));
    },
  });

  const selectThread = useCallback(
    (threadId: string) => {
      const thread = threads.find((item) => item.id === threadId);
      applyThreadSelection(threadId, thread?.active_revision_id, {
        navigateToChat: true,
      });
    },
    [applyThreadSelection, threads],
  );

  const createThread = useCallback(() => {
    if (
      !organizationId ||
      !agentId ||
      createThreadMutation.isPending ||
      createFromGoalMutation.isPending
    ) {
      return;
    }
    createThreadMutation.mutate();
  }, [
    agentId,
    createFromGoalMutation.isPending,
    createThreadMutation,
    organizationId,
  ]);

  const createThreadFromGoal = useCallback(
    (
      goal: StudioFromGoalDraft,
      options?: { onSuccess?: () => void },
    ) => {
      if (
        !organizationId ||
        !agentId ||
        createThreadMutation.isPending ||
        createFromGoalMutation.isPending
      ) {
        return;
      }
      createFromGoalMutation.mutate(goal, {
        onSuccess: () => options?.onSuccess?.(),
      });
    },
    [
      agentId,
      createFromGoalMutation,
      createThreadMutation.isPending,
      organizationId,
    ],
  );

  const listError =
    threadsQuery.error instanceof Error
      ? threadsQuery.error.message
      : threadsQuery.error
        ? String(threadsQuery.error)
        : null;

  return {
    organizationId,
    agentId,
    canWriteRecipes,
    agentAlias,
    threads,
    threadsQuery,
    pullQuery,
    selectedThreadId,
    createThread,
    createThreadFromGoal,
    createThreadPending: createThreadMutation.isPending,
    createFromGoalPending: createFromGoalMutation.isPending,
    selectThread,
    listError,
    threadsQueryKey,
  };
}
